/**
 * What an UNSET Studio colour token actually renders as in the customer app.
 *
 * VRG-720. These exist because Studio's swatches and the app's fallbacks were two
 * independent sets of literals, and two of the four disagreed — so an unconfigured
 * brand was shown a colour the product does not use:
 *
 * | token              | Studio swatch said | app rendered |
 * |--------------------|--------------------|--------------|
 * | `primary_color`    | `#000000`          | `#000000`    |
 * | `text_color`       | `#000000`          | `#000000`    |
 * | `secondary_color`  | `#000000`          | **`#E4E4E7`**|
 * | `bg_color`         | `#ffffff`          | **`#F7F7F8`**|
 *
 * The background one is what made the Studio preview look wrong: the swatch read
 * `#ffffff` while the preview rendered `#F7F7F8`, so the preview looked like it was
 * failing to inherit. It was not — the preview was right and the swatch was wrong, and
 * typing `#ffffff` into the field "fixed" it only by writing a value that had never
 * been stored. The border one is the louder lie: black against a light-grey hairline.
 *
 * Both halves now read from here. The app's `settings/mui/palette.ts` is still where the
 * fallback is APPLIED; this module is only the agreed value, so a change to one side
 * cannot silently drift from the other.
 */
export const STUDIO_THEME_DEFAULTS = {
  /** `palette.primary.main` / `text.heading`. */
  primary_color: '#000000',
  /** `palette.text.primary`. */
  text_color: '#000000',
  /**
   * `palette.secondary.main`, the divider/panel hairline — NOT black. Studio labels this
   * "Border color", which is what made `#000000` look plausible in the swatch.
   */
  secondary_color: '#E4E4E7',
  /**
   * `palette.background.default`. A soft grey, not pure white: white cards on a white
   * page are separated by a 1px border and nothing else, which is what read as flat.
   */
  bg_color: '#F7F7F8',
} as const;

export type StudioThemeDefaultToken = keyof typeof STUDIO_THEME_DEFAULTS;
